<template>
  <v-dialog
    :value="value"
    max-width="1200"
    persistent
    scrollable
    @input="handleDialogInput"
  >
    <v-card class="recommendation-card">
      <div class="recommendation-header">
        <div class="header-title-group">
          <div class="header-icon">
            <v-icon color="#2c3e50">star</v-icon>
          </div>
          <div>
            <h2 class="header-title">Food Search Recommendations</h2>
            <p class="header-subtitle">
              Manage recommended stores and menus shown in customer search
            </p>
          </div>
        </div>
        <div class="header-actions">
          <v-chip color="#fddf00" text-color="#2c3e50" class="province-chip">
            <v-icon small left>place</v-icon>
            {{ provinceLabel }}
          </v-chip>
          <v-btn icon class="app-icon-button" aria-label="Close" @click="close">
            <v-icon>close</v-icon>
          </v-btn>
        </div>
      </div>

      <v-card-text class="recommendation-content">
        <div class="summary-grid">
          <div class="summary-card">
            <div class="summary-icon summary-icon--all">
              <v-icon>view_list</v-icon>
            </div>
            <div>
              <p class="summary-value">{{ items.length }}</p>
              <p class="summary-label">All recommendations</p>
            </div>
          </div>
          <div class="summary-card">
            <div class="summary-icon summary-icon--store">
              <v-icon>store</v-icon>
            </div>
            <div>
              <p class="summary-value">{{ storeCount }}</p>
              <p class="summary-label">Stores</p>
            </div>
          </div>
          <div class="summary-card">
            <div class="summary-icon summary-icon--menu">
              <v-icon>restaurant_menu</v-icon>
            </div>
            <div>
              <p class="summary-value">{{ menuCount }}</p>
              <p class="summary-label">Menus</p>
            </div>
          </div>
          <div class="summary-card">
            <div class="summary-icon summary-icon--active">
              <v-icon>check_circle</v-icon>
            </div>
            <div>
              <p class="summary-value">{{ activeCount }}</p>
              <p class="summary-label">Active now</p>
            </div>
          </div>
        </div>

        <v-card class="filter-card" elevation="2">
          <v-card-text class="filter-content">
            <div class="filter-heading">
              <div>
                <h3 class="section-title">Recommendation Search &amp; Filters</h3>
                <p class="section-subtitle">
                  Find recommendations by ID, type, or status
                </p>
              </div>
              <v-btn
                depressed
                color="#fddf00"
                class="app-action app-action--primary add-button"
                @click="openCreateForm"
              >
                <v-icon left>add</v-icon>
                Add Recommendation
              </v-btn>
            </div>

            <div class="filter-grid">
              <div class="filter-control">
                <label class="filter-label">
                  <v-icon small>search</v-icon>
                  Store/Menu name
                </label>
                <v-text-field
                  v-model="search"
                  placeholder="Search by name"
                  solo
                  flat
                  clearable
                  hide-details
                  color="#fddf00"
                  class="filter-input"
                />
              </div>
              <div class="filter-control">
                <label class="filter-label">
                  <v-icon small>category</v-icon>
                  Type
                </label>
                <v-select
                  v-model="entityTypeFilter"
                  :items="entityTypeFilters"
                  solo
                  flat
                  hide-details
                  color="#fddf00"
                  class="filter-input"
                />
              </div>
              <div class="filter-control">
                <label class="filter-label">
                  <v-icon small>toggle_on</v-icon>
                  Status
                </label>
                <v-select
                  v-model="statusFilter"
                  :items="statusFilters"
                  solo
                  flat
                  hide-details
                  color="#fddf00"
                  class="filter-input"
                />
              </div>
              <v-btn
                flat
                color="grey darken-1"
                class="app-action app-action--ghost clear-button"
                :disabled="!hasActiveFilters"
                @click="clearFilters"
              >
                <v-icon small left>clear</v-icon>
                Clear filters
              </v-btn>
            </div>
          </v-card-text>
        </v-card>

        <v-card class="table-card" elevation="2">
          <v-card-title class="table-header">
            <div>
              <h3 class="section-title">Recommendation List</h3>
              <p class="section-subtitle">
                {{ filteredItems.length }} recommendations found
              </p>
            </div>
            <v-btn
              icon
              class="app-icon-button"
              :loading="loading"
              title="Refresh"
              @click="load"
            >
              <v-icon>refresh</v-icon>
            </v-btn>
          </v-card-title>

          <v-data-table
            :headers="headers"
            :items="filteredItems"
            :loading="loading"
            :pagination.sync="pagination"
            :rows-per-page-items="[10, 20, 50]"
            class="modern-table"
          >
            <template slot="items" slot-scope="props">
              <tr class="table-row">
                <td class="table-cell entity-name-cell">
                  <div class="entity-cell">
                    <div
                      class="entity-icon"
                      :class="`entity-icon--${props.item.entity_type}`"
                    >
                      <v-icon small>{{ entityIcon(props.item.entity_type) }}</v-icon>
                    </div>
                    <div class="entity-name-content">
                      <div class="entity-name">
                        {{ props.item.entity_name || `Unknown ${entityLabel(props.item.entity_type)}` }}
                      </div>
                      <div class="entity-meta">
                        <span v-if="props.item.entity_name_en">
                          {{ props.item.entity_name_en }} ·
                        </span>
                        <span v-if="props.item.store_name">
                          {{ props.item.store_name }} ·
                        </span>
                        ID {{ props.item.entity_id }}
                      </div>
                    </div>
                  </div>
                </td>
                <td class="table-cell text-xs-center">
                    <v-chip
                      small
                      :color="entityColor(props.item.entity_type)"
                      text-color="white"
                    >
                      {{ entityLabel(props.item.entity_type) }}
                    </v-chip>
                </td>
                <td class="table-cell text-xs-center">
                  <v-chip small color="blue-grey lighten-5">
                    <v-icon small left>vertical_align_top</v-icon>
                    {{ props.item.priority }}
                  </v-chip>
                </td>
                <td class="table-cell text-xs-center">
                  <v-chip
                    small
                    :color="statusColor(props.item)"
                    :text-color="statusTextColor(props.item)"
                  >
                    <v-icon small left>{{ statusIcon(props.item) }}</v-icon>
                    {{ statusLabel(props.item) }}
                  </v-chip>
                </td>
                <td class="table-cell schedule-cell">
                  <div class="schedule-value schedule-value--start">
                    <v-icon small>event_available</v-icon>
                    <span>{{ formatDateTime(props.item.starts_at, "Always") }}</span>
                  </div>
                  <div class="schedule-value schedule-value--end">
                    <v-icon small>event_busy</v-icon>
                    <span>{{ formatDateTime(props.item.ends_at, "No end date") }}</span>
                  </div>
                </td>
                <td class="table-cell text-xs-center">
                  <div class="action-buttons">
                    <v-tooltip top>
                      <v-btn
                        slot="activator"
                        icon
                        small
                        class="app-icon-button app-icon-button--edit"
                        aria-label="Edit recommendation"
                        @click="openEditForm(props.item)"
                      >
                        <v-icon small>edit</v-icon>
                      </v-btn>
                      <span>Edit</span>
                    </v-tooltip>
                    <v-tooltip top>
                      <v-btn
                        slot="activator"
                        icon
                        small
                        class="app-icon-button app-icon-button--danger"
                        aria-label="Delete recommendation"
                        :loading="deletingKey === itemKey(props.item)"
                        @click="remove(props.item)"
                      >
                        <v-icon small>delete_outline</v-icon>
                      </v-btn>
                      <span>Delete</span>
                    </v-tooltip>
                  </div>
                </td>
              </tr>
            </template>

            <template slot="no-data">
              <div class="empty-state">
                <v-progress-circular
                  v-if="loading"
                  indeterminate
                  color="#fddf00"
                  :size="36"
                />
                <template v-else>
                  <v-icon size="72" color="#fddf00">star_border</v-icon>
                  <h3>No recommendations found</h3>
                  <p>
                    {{
                      hasActiveFilters
                        ? "Try adjusting your search filters"
                        : "Add a store or menu to start curating customer search"
                    }}
                  </p>
                  <v-btn
                    v-if="!hasActiveFilters"
                    color="#fddf00"
                    class="app-action app-action--primary add-button"
                    @click="openCreateForm"
                  >
                    <v-icon left>add</v-icon>
                    Add Recommendation
                  </v-btn>
                </template>
              </div>
            </template>
          </v-data-table>
        </v-card>
      </v-card-text>

      <v-card-actions class="footer-actions">
        <v-spacer />
        <v-btn flat class="app-action app-action--secondary" @click="close">
          Close
        </v-btn>
      </v-card-actions>

      <v-dialog v-model="formDialog" max-width="760" persistent>
        <v-card class="form-card">
          <div class="form-header">
            <div>
              <h2 class="form-title">
                {{ editing ? "Edit Recommendation" : "Add Recommendation" }}
              </h2>
              <p class="form-subtitle">
                Configure which store or menu appears for {{ provinceLabel }}
              </p>
            </div>
            <v-btn icon class="app-icon-button" @click="closeForm">
              <v-icon>close</v-icon>
            </v-btn>
          </div>

          <v-card-text class="form-content">
            <v-form ref="form" v-model="formValid" lazy-validation>
              <div class="form-grid">
                <div class="candidate-control">
                  <label class="candidate-label">
                    <v-icon small>category</v-icon>
                    Recommendation type
                  </label>
                  <v-select
                    v-model="form.entity_type"
                    :items="entityTypes"
                    :disabled="editing"
                    solo
                    flat
                    hide-details
                    color="#fddf00"
                    class="candidate-input"
                  />
                </div>
                <div class="candidate-control">
                  <label class="candidate-label">
                    <v-icon small>{{ entityIcon(form.entity_type) }}</v-icon>
                    {{ form.entity_type === "menu" ? "Menu name" : "Store name" }}
                  </label>
                  <v-autocomplete
                    v-model="selectedCandidate"
                    :items="candidateItems"
                    :loading="candidateLoading"
                    :search-input.sync="candidateSearch"
                    :disabled="editing"
                    :rules="candidateRules"
                    item-text="display_name"
                    item-value="id"
                    return-object
                    no-filter
                    clearable
                    hide-selected
                    placeholder="Type at least 2 characters"
                    solo
                    flat
                    color="#fddf00"
                    class="candidate-input"
                  >
                    <template slot="item" slot-scope="data">
                      <v-list-tile-content>
                        <v-list-tile-title>{{ data.item.name }}</v-list-tile-title>
                        <v-list-tile-sub-title>
                          <span v-if="data.item.name_en">{{ data.item.name_en }}</span>
                          <span v-if="data.item.name_en && data.item.store_name"> · </span>
                          <span v-if="data.item.store_name">ร้าน {{ data.item.store_name }}</span>
                          <span> · ID {{ data.item.id }}</span>
                        </v-list-tile-sub-title>
                      </v-list-tile-content>
                    </template>
                    <template slot="no-data">
                      <v-list-tile>
                        <v-list-tile-content>
                          <v-list-tile-title>
                            {{ candidateNoDataText }}
                          </v-list-tile-title>
                        </v-list-tile-content>
                      </v-list-tile>
                    </template>
                  </v-autocomplete>
                </div>
                <v-text-field
                  v-model.number="form.priority"
                  :rules="priorityRules"
                  type="number"
                  label="Priority"
                  prepend-icon="vertical_align_top"
                  hint="Higher numbers appear first"
                  persistent-hint
                  outline
                  color="#fddf00"
                />
                <div class="enabled-field">
                  <v-switch
                    v-model="form.enabled"
                    color="#fddf00"
                    label="Enabled"
                    hide-details
                  />
                  <p>Disabled items remain saved but are hidden from customers.</p>
                </div>
                <div class="schedule-picker-field">
                  <label class="candidate-label">
                    <v-icon small color="green darken-2">event_available</v-icon>
                    Start date and time
                  </label>
                  <div class="schedule-picker-grid">
                    <v-menu
                      v-model="startDateMenu"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      full-width
                      min-width="290px"
                    >
                      <v-text-field
                        slot="activator"
                        :value="formatScheduleDate(form.starts_at)"
                        placeholder="Select date"
                        prepend-inner-icon="event"
                        readonly
                        clearable
                        solo
                        flat
                        hide-details
                        class="schedule-picker-input"
                        @click:clear.stop="clearSchedule('starts_at')"
                      />
                      <v-date-picker
                        :value="scheduleDate(form.starts_at)"
                        color="#fddf00"
                        @input="setScheduleDate('starts_at', $event); startDateMenu = false"
                      />
                    </v-menu>
                    <v-menu
                      v-model="startTimeMenu"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      full-width
                      min-width="290px"
                    >
                      <v-text-field
                        slot="activator"
                        :value="scheduleTime(form.starts_at)"
                        :disabled="!form.starts_at"
                        placeholder="Time"
                        prepend-inner-icon="access_time"
                        readonly
                        solo
                        flat
                        hide-details
                        class="schedule-picker-input"
                      />
                      <v-time-picker
                        :value="scheduleTime(form.starts_at)"
                        color="#fddf00"
                        format="24hr"
                        @input="setScheduleTime('starts_at', $event)"
                        @change="startTimeMenu = false"
                      />
                    </v-menu>
                  </div>
                </div>
                <div class="schedule-picker-field">
                  <label class="candidate-label">
                    <v-icon small color="orange darken-3">event_busy</v-icon>
                    End date and time
                  </label>
                  <div class="schedule-picker-grid">
                    <v-menu
                      v-model="endDateMenu"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      full-width
                      min-width="290px"
                    >
                      <v-text-field
                        slot="activator"
                        :value="formatScheduleDate(form.ends_at)"
                        placeholder="Select date"
                        prepend-inner-icon="event"
                        readonly
                        clearable
                        solo
                        flat
                        hide-details
                        class="schedule-picker-input"
                        @click:clear.stop="clearSchedule('ends_at')"
                      />
                      <v-date-picker
                        :value="scheduleDate(form.ends_at)"
                        color="#fddf00"
                        @input="setScheduleDate('ends_at', $event); endDateMenu = false"
                      />
                    </v-menu>
                    <v-menu
                      v-model="endTimeMenu"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      full-width
                      min-width="290px"
                    >
                      <v-text-field
                        slot="activator"
                        :value="scheduleTime(form.ends_at)"
                        :disabled="!form.ends_at"
                        placeholder="Time"
                        prepend-inner-icon="access_time"
                        readonly
                        solo
                        flat
                        hide-details
                        class="schedule-picker-input"
                      />
                      <v-time-picker
                        :value="scheduleTime(form.ends_at)"
                        color="#fddf00"
                        format="24hr"
                        @input="setScheduleTime('ends_at', $event)"
                        @change="endTimeMenu = false"
                      />
                    </v-menu>
                  </div>
                  <p v-if="scheduleError" class="schedule-error">{{ scheduleError }}</p>
                </div>
              </div>
            </v-form>
          </v-card-text>

          <v-card-actions class="form-actions">
            <v-spacer />
            <v-btn
              flat
              class="app-action app-action--secondary"
              :disabled="saving"
              @click="closeForm"
            >
              Cancel
            </v-btn>
            <v-btn
              depressed
              color="#fddf00"
              class="app-action app-action--primary save-button"
              :loading="saving"
              :disabled="!formValid"
              @click="save"
            >
              <v-icon left>save</v-icon>
              {{ editing ? "Save Changes" : "Add Recommendation" }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-snackbar v-model="successSnackbar" color="success" :timeout="3000">
        <v-icon color="white">check_circle</v-icon>
        {{ successMessage }}
        <v-btn flat color="white" @click="successSnackbar = false">Close</v-btn>
      </v-snackbar>

      <v-snackbar v-model="errorSnackbar" color="error" :timeout="4000">
        <v-icon color="white">error</v-icon>
        {{ errorMessage }}
        <v-btn flat color="white" @click="errorSnackbar = false">Close</v-btn>
      </v-snackbar>
    </v-card>
  </v-dialog>
</template>

<script>
import _ from "lodash";
import moment from "moment";
import { globalAxios } from "@/axios-path.js";

const emptyForm = () => {
  const now = moment();
  return {
    entity_type: "store",
    entity_id: null,
    priority: 0,
    enabled: true,
    starts_at: now.format("YYYY-MM-DDTHH:mm"),
    ends_at: now.clone().add(1, "day").format("YYYY-MM-DDTHH:mm"),
  };
};

export default {
  props: {
    value: { type: Boolean, default: false },
    province: { type: String, required: true },
  },
  data: () => ({
    items: [],
    form: emptyForm(),
    loading: false,
    saving: false,
    deletingKey: null,
    formDialog: false,
    formValid: true,
    editing: false,
    candidateItems: [],
    selectedCandidate: null,
    candidateSearch: "",
    candidateLoading: false,
    candidateRequestId: 0,
    startDateMenu: false,
    startTimeMenu: false,
    endDateMenu: false,
    endTimeMenu: false,
    search: "",
    entityTypeFilter: "all",
    statusFilter: "all",
    scheduleError: "",
    successSnackbar: false,
    successMessage: "",
    errorSnackbar: false,
    errorMessage: "",
    pagination: {
      page: 1,
      rowsPerPage: 10,
      sortBy: "priority",
      descending: true,
    },
    entityTypes: [
      { text: "Store", value: "store" },
      { text: "Menu", value: "menu" },
    ],
    entityTypeFilters: [
      { text: "All types", value: "all" },
      { text: "Stores", value: "store" },
      { text: "Menus", value: "menu" },
    ],
    statusFilters: [
      { text: "All statuses", value: "all" },
      { text: "Active now", value: "active" },
      { text: "Scheduled", value: "scheduled" },
      { text: "Expired", value: "expired" },
      { text: "Disabled", value: "disabled" },
    ],
    candidateRules: [
      (value) => Boolean(value && value.id) || "Select a store or menu by name",
    ],
    priorityRules: [
      (value) => Number.isInteger(Number(value)) || "Priority must be a number",
    ],
    headers: [
      { text: "Name", value: "entity_name" },
      { text: "Type", value: "entity_type", align: "center" },
      { text: "Priority", value: "priority", align: "center" },
      { text: "Status", value: "enabled", align: "center", sortable: false },
      { text: "Schedule", value: "starts_at", sortable: false },
      { text: "Actions", value: "actions", align: "center", sortable: false },
    ],
  }),
  computed: {
    provinceLabel() {
      if (!this.province) return "Unknown province";
      return this.province.charAt(0).toUpperCase() + this.province.slice(1);
    },
    storeCount() {
      return this.items.filter((item) => item.entity_type === "store").length;
    },
    menuCount() {
      return this.items.filter((item) => item.entity_type === "menu").length;
    },
    activeCount() {
      return this.items.filter((item) => this.itemStatus(item) === "active").length;
    },
    candidateNoDataText() {
      if (!this.candidateSearch || this.candidateSearch.trim().length < 2) {
        return "Type at least 2 characters to search by name";
      }
      return this.candidateLoading ? "Searching..." : "No matching results";
    },
    hasActiveFilters() {
      return Boolean(this.search) || this.entityTypeFilter !== "all" || this.statusFilter !== "all";
    },
    filteredItems() {
      const search = String(this.search || "").trim().toLowerCase();
      return this.items.filter((item) => {
        const matchesSearch = !search ||
          String(item.entity_id).includes(search) ||
          String(item.entity_name || "").toLowerCase().includes(search) ||
          String(item.entity_name_en || "").toLowerCase().includes(search) ||
          String(item.store_name || "").toLowerCase().includes(search) ||
          this.entityLabel(item.entity_type).toLowerCase().includes(search);
        const matchesType = this.entityTypeFilter === "all" ||
          item.entity_type === this.entityTypeFilter;
        const matchesStatus = this.statusFilter === "all" ||
          this.itemStatus(item) === this.statusFilter;
        return matchesSearch && matchesType && matchesStatus;
      });
    },
  },
  watch: {
    value(open) {
      if (open) this.load();
    },
    filteredItems() {
      this.pagination.page = 1;
    },
    selectedCandidate(candidate) {
      if (candidate) this.form.entity_id = candidate.id;
      else if (!this.editing) this.form.entity_id = null;
    },
    "form.entity_type"(next, previous) {
      if (!this.formDialog || this.editing || next === previous) return;
      this.selectedCandidate = null;
      this.candidateItems = [];
      this.candidateSearch = "";
      this.candidateRequestId += 1;
      this.form.entity_id = null;
    },
    candidateSearch(query) {
      if (
        this.selectedCandidate &&
        query === this.selectedCandidate.display_name
      ) return;
      this.searchCandidates(query);
    },
  },
  methods: {
    endpoint() {
      return `/api/v1/${this.province}/food-search/v1/recommendations`;
    },
    candidateEndpoint() {
      return `${this.endpoint()}/candidates`;
    },
    handleDialogInput(open) {
      if (!open) this.close();
    },
    close() {
      this.closeForm();
      this.$emit("input", false);
    },
    async load() {
      this.loading = true;
      try {
        const response = await globalAxios.get(this.endpoint());
        const payload = response && response.data ? response.data : response;
        this.items = payload && payload.items ? payload.items : [];
      } catch (error) {
        this.showError("Unable to load search recommendations");
      } finally {
        this.loading = false;
      }
    },
    openCreateForm() {
      this.editing = false;
      this.form = emptyForm();
      this.selectedCandidate = null;
      this.candidateItems = [];
      this.candidateSearch = "";
      this.candidateRequestId += 1;
      this.scheduleError = "";
      this.formDialog = true;
      this.$nextTick(() => {
        if (this.$refs.form) this.$refs.form.resetValidation();
      });
    },
    openEditForm(item) {
      this.candidateRequestId += 1;
      this.candidateLoading = false;
      this.editing = true;
      this.form = {
        ...item,
        starts_at: item.starts_at ? item.starts_at.slice(0, 16) : null,
        ends_at: item.ends_at ? item.ends_at.slice(0, 16) : null,
      };
      this.selectedCandidate = {
        id: item.entity_id,
        entity_type: item.entity_type,
        name: item.entity_name || `Unknown ${this.entityLabel(item.entity_type)}`,
        name_en: item.entity_name_en || "",
        store_name: item.store_name || "",
        display_name: item.entity_name || `Unknown ${this.entityLabel(item.entity_type)}`,
      };
      this.candidateItems = [this.selectedCandidate];
      this.candidateSearch = this.selectedCandidate.display_name;
      this.scheduleError = "";
      this.formDialog = true;
      this.$nextTick(() => {
        if (this.$refs.form) this.$refs.form.resetValidation();
      });
    },
    closeForm() {
      if (this.saving) return;
      this.closeSchedulePickers();
      this.formDialog = false;
      this.editing = false;
      this.form = emptyForm();
      this.selectedCandidate = null;
      this.candidateItems = [];
      this.candidateSearch = "";
      this.candidateRequestId += 1;
      this.candidateLoading = false;
      this.scheduleError = "";
    },
    searchCandidates: _.debounce(async function(query) {
      const term = String(query || "").trim();
      const requestId = ++this.candidateRequestId;
      if (this.editing || term.length < 2) {
        this.candidateItems = [];
        this.candidateLoading = false;
        return;
      }
      this.candidateLoading = true;
      try {
        const response = await globalAxios.get(this.candidateEndpoint(), {
          params: { q: term, type: this.form.entity_type },
        });
        const payload = response && response.data ? response.data : response;
        if (requestId === this.candidateRequestId) {
          this.candidateItems = ((payload && payload.items) || [])
            .filter((item) => !this.isAlreadyRecommended(item))
            .map((item) => ({
              ...item,
              display_name: this.candidateDisplayName(item),
            }));
        }
      } catch (error) {
        if (requestId === this.candidateRequestId) {
          this.candidateItems = [];
          this.showError("Unable to search stores and menus");
        }
      } finally {
        if (requestId === this.candidateRequestId) {
          this.candidateLoading = false;
        }
      }
    }, 300),
    isAlreadyRecommended(candidate) {
      return this.items.some((item) =>
        item.entity_type === candidate.entity_type &&
        Number(item.entity_id) === Number(candidate.id)
      );
    },
    candidateDisplayName(item) {
      if (item.entity_type === "menu" && item.store_name) {
        return `${item.name} — ${item.store_name}`;
      }
      return item.name;
    },
    scheduleDate(value) {
      return value && moment(value).isValid() ? moment(value).format("YYYY-MM-DD") : "";
    },
    scheduleTime(value) {
      return value && moment(value).isValid() ? moment(value).format("HH:mm") : "";
    },
    formatScheduleDate(value) {
      return value && moment(value).isValid() ? moment(value).format("DD MMM YYYY") : "";
    },
    setScheduleDate(field, date) {
      if (!date) {
        this.clearSchedule(field);
        return;
      }
      const time = this.scheduleTime(this.form[field]) || "00:00";
      this.form[field] = `${date}T${time}`;
      this.validateSchedule();
    },
    setScheduleTime(field, time) {
      const date = this.scheduleDate(this.form[field]);
      if (!date || !time) return;
      this.form[field] = `${date}T${time}`;
      this.validateSchedule();
    },
    clearSchedule(field) {
      this.form[field] = null;
      this.scheduleError = "";
      if (field === "starts_at") {
        this.startDateMenu = false;
        this.startTimeMenu = false;
      } else {
        this.endDateMenu = false;
        this.endTimeMenu = false;
      }
    },
    closeSchedulePickers() {
      this.startDateMenu = false;
      this.startTimeMenu = false;
      this.endDateMenu = false;
      this.endTimeMenu = false;
    },
    validateSchedule() {
      this.scheduleError = "";
      if (
        this.form.starts_at &&
        this.form.ends_at &&
        moment(this.form.ends_at).isBefore(moment(this.form.starts_at))
      ) {
        this.scheduleError = "End date must be after the start date";
        return false;
      }
      return true;
    },
    async save() {
      const valid = this.$refs.form ? this.$refs.form.validate() : true;
      if (!valid || !this.validateSchedule()) return;

      this.saving = true;
      try {
        await globalAxios.post(this.endpoint(), this.form);
        this.formDialog = false;
        this.form = emptyForm();
        this.selectedCandidate = null;
        this.candidateItems = [];
        this.candidateSearch = "";
        this.candidateRequestId += 1;
        this.candidateLoading = false;
        this.editing = false;
        await this.load();
        this.showSuccess("Recommendation saved");
      } catch (error) {
        this.showError("Unable to save this recommendation");
      } finally {
        this.saving = false;
      }
    },
    async remove(item) {
      const confirmed = window.confirm(
        `Delete ${item.entity_name || this.entityLabel(item.entity_type)} from recommendations?`
      );
      if (!confirmed) return;

      this.deletingKey = this.itemKey(item);
      try {
        await globalAxios.delete(this.endpoint(), {
          params: { entity_type: item.entity_type, entity_id: item.entity_id },
        });
        await this.load();
        this.showSuccess("Recommendation deleted");
      } catch (error) {
        this.showError("Unable to delete this recommendation");
      } finally {
        this.deletingKey = null;
      }
    },
    clearFilters() {
      this.search = "";
      this.entityTypeFilter = "all";
      this.statusFilter = "all";
    },
    itemKey(item) {
      return `${item.entity_type}-${item.entity_id}`;
    },
    itemStatus(item) {
      if (!item.enabled) return "disabled";
      const now = moment();
      if (item.starts_at && moment(item.starts_at).isAfter(now)) return "scheduled";
      if (item.ends_at && moment(item.ends_at).isBefore(now)) return "expired";
      return "active";
    },
    statusLabel(item) {
      const labels = {
        active: "Active",
        scheduled: "Scheduled",
        expired: "Expired",
        disabled: "Disabled",
      };
      return labels[this.itemStatus(item)];
    },
    statusColor(item) {
      const colors = {
        active: "green lighten-4",
        scheduled: "amber lighten-3",
        expired: "grey lighten-2",
        disabled: "red lighten-4",
      };
      return colors[this.itemStatus(item)];
    },
    statusTextColor(item) {
      return this.itemStatus(item) === "disabled" ? "red darken-3" : "grey darken-4";
    },
    statusIcon(item) {
      const icons = {
        active: "check_circle",
        scheduled: "schedule",
        expired: "history",
        disabled: "cancel",
      };
      return icons[this.itemStatus(item)];
    },
    entityLabel(type) {
      return type === "menu" ? "Menu" : "Store";
    },
    entityIcon(type) {
      return type === "menu" ? "restaurant_menu" : "store";
    },
    entityColor(type) {
      return type === "menu" ? "deep-orange lighten-1" : "blue lighten-1";
    },
    formatDateTime(value, fallback) {
      return value ? moment(value).format("DD MMM YYYY, HH:mm") : fallback;
    },
    showError(message) {
      this.errorMessage = message;
      this.errorSnackbar = true;
    },
    showSuccess(message) {
      this.successMessage = message;
      this.successSnackbar = true;
    },
  },
};
</script>

<style scoped>
.recommendation-card {
  border-radius: 16px;
  overflow: hidden;
}

.recommendation-header,
.form-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 24px 28px;
  border-bottom: 1px solid #eceff1;
  background: #fff;
}

.header-title-group,
.header-actions,
.entity-cell,
.action-buttons,
.schedule-value {
  display: flex;
  align-items: center;
}

.header-title-group {
  gap: 16px;
}

.header-actions {
  gap: 8px;
}

.header-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: #fddf00;
}

.header-title,
.form-title {
  margin: 0 0 4px;
  color: #2c3e50;
  font-size: 24px;
  font-weight: 700;
}

.header-subtitle,
.form-subtitle,
.section-subtitle {
  margin: 0;
  color: #7f8c8d;
}

.province-chip {
  font-weight: 600;
}

.recommendation-content {
  padding: 28px;
  background: #f7f8fa;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.summary-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px;
  border: 1px solid rgba(253, 223, 0, 0.25);
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 2px 8px rgba(44, 62, 80, 0.06);
}

.summary-icon,
.entity-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
}

.summary-icon {
  width: 42px;
  height: 42px;
}

.summary-icon--all { background: #fff8c5; }
.summary-icon--store { background: #e3f2fd; }
.summary-icon--menu { background: #fbe9e7; }
.summary-icon--active { background: #e8f5e9; }

.summary-value {
  margin: 0;
  color: #2c3e50;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.1;
}

.summary-label {
  margin: 4px 0 0;
  color: #7f8c8d;
  font-size: 13px;
}

.filter-card,
.table-card {
  border: 1px solid rgba(253, 223, 0, 0.2);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.96);
}

.filter-card {
  margin-bottom: 24px;
}

.filter-content {
  padding: 24px;
}

.filter-heading,
.table-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
}

.filter-heading {
  margin-bottom: 22px;
}

.section-title {
  margin: 0 0 4px;
  color: #2c3e50;
  font-size: 20px;
  font-weight: 600;
}

.app-action {
  min-height: 40px;
  border-radius: 10px;
  font-weight: 600;
  letter-spacing: 0;
  text-transform: none;
}

.app-action--primary,
.add-button,
.save-button {
  color: #1f2933 !important;
  box-shadow: 0 6px 16px rgba(253, 223, 0, 0.26) !important;
}

.app-action--secondary,
.app-action--ghost {
  border: 1px solid rgba(120, 144, 156, 0.3);
  background: rgba(255, 255, 255, 0.68) !important;
  color: #455a64 !important;
  backdrop-filter: blur(10px);
}

.app-icon-button {
  border: 1px solid rgba(120, 144, 156, 0.22);
  background: rgba(255, 255, 255, 0.72) !important;
  box-shadow: 0 4px 12px rgba(44, 62, 80, 0.08);
  backdrop-filter: blur(10px);
}

.app-icon-button--edit {
  border-color: rgba(33, 150, 243, 0.25);
  background: #e3f2fd !important;
  color: #1565c0 !important;
}

.app-icon-button--danger {
  border-color: rgba(244, 67, 54, 0.22);
  background: #ffebee !important;
  color: #c62828 !important;
}

.action-buttons .app-icon-button {
  width: 36px;
  height: 36px;
  margin: 0 4px;
  border-radius: 10px !important;
  box-shadow: none;
}

.action-buttons .app-icon-button:hover {
  box-shadow: 0 4px 10px rgba(44, 62, 80, 0.12);
  transform: translateY(-1px);
}

.action-buttons .app-icon-button--edit:hover {
  background: #bbdefb !important;
}

.action-buttons .app-icon-button--danger:hover {
  background: #ffcdd2 !important;
}

.action-buttons .app-icon-button >>> .v-icon {
  color: inherit !important;
}

.filter-grid {
  display: grid;
  grid-template-columns: minmax(240px, 2fr) minmax(170px, 1fr) minmax(170px, 1fr) auto;
  align-items: end;
  gap: 16px;
}

.filter-control {
  min-width: 0;
}

.filter-label {
  display: flex;
  align-items: center;
  gap: 7px;
  margin: 0 0 7px 2px;
  color: #546e7a;
  font-size: 13px;
  font-weight: 600;
}

.filter-input >>> .v-input__slot {
  min-height: 52px;
  margin-bottom: 0;
  border: 1px solid #cfd8dc;
  border-radius: 8px;
  background: #fff !important;
  box-shadow: none !important;
}

.filter-input.v-input--is-focused >>> .v-input__slot {
  border-color: #d7bd00;
  box-shadow: 0 0 0 2px rgba(253, 223, 0, 0.18) !important;
}

.clear-button {
  text-transform: none;
}

.table-header {
  padding: 22px 24px;
  border-bottom: 1px solid #eceff1;
}

.modern-table >>> thead th {
  color: #455a64 !important;
  font-weight: 600 !important;
  background: #fafafa;
}

.table-row:hover {
  background: #fffdf0 !important;
}

.table-cell {
  padding-top: 12px !important;
  padding-bottom: 12px !important;
}

.entity-cell {
  justify-content: flex-start;
  gap: 8px;
}

.entity-name-cell {
  min-width: 250px;
}

.entity-name-content {
  min-width: 0;
}

.entity-name {
  overflow: hidden;
  color: #263238;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.entity-meta {
  overflow: hidden;
  margin-top: 2px;
  color: #90a4ae;
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.entity-icon {
  width: 30px;
  height: 30px;
}

.entity-icon--store { background: #e3f2fd; }
.entity-icon--menu { background: #fbe9e7; }

.entity-id {
  color: #37474f;
  font-weight: 700;
}

.schedule-cell {
  min-width: 220px;
}

.schedule-value {
  gap: 7px;
  margin: 3px 0;
  color: #546e7a;
  font-size: 13px;
}

.schedule-value--start >>> .v-icon {
  color: #2e7d32;
}

.schedule-value--end >>> .v-icon {
  color: #ef6c00;
}

.action-buttons {
  justify-content: center;
  gap: 4px;
}

.empty-state {
  padding: 48px 24px;
  text-align: center;
}

.empty-state h3 {
  margin: 12px 0 6px;
  color: #2c3e50;
}

.empty-state p {
  margin: 0 0 16px;
  color: #7f8c8d;
}

.footer-actions,
.form-actions {
  padding: 14px 24px;
  border-top: 1px solid #eceff1;
  background: #fff;
}

.form-card {
  border-radius: 14px;
}

.form-content {
  padding: 28px;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px 20px;
}

.candidate-control {
  min-width: 0;
}

.candidate-label {
  display: flex;
  align-items: center;
  gap: 7px;
  margin: 0 0 7px 2px;
  color: #546e7a;
  font-size: 13px;
  font-weight: 600;
}

.candidate-input >>> .v-input__slot {
  min-height: 56px;
  border: 1px solid #cfd8dc;
  border-radius: 8px;
  background: #fff !important;
  box-shadow: none !important;
}

.candidate-input.v-input--is-focused >>> .v-input__slot {
  border-color: #d7bd00;
  box-shadow: 0 0 0 2px rgba(253, 223, 0, 0.18) !important;
}

.schedule-picker-field {
  min-width: 0;
  margin-top: 4px;
}

.schedule-picker-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.45fr) minmax(105px, 0.8fr);
  gap: 8px;
}

.schedule-picker-input >>> .v-input__slot {
  min-height: 52px;
  margin-bottom: 0;
  border: 1px solid #cfd8dc;
  border-radius: 8px;
  background: #fff !important;
  box-shadow: none !important;
}

.schedule-picker-input.v-input--is-focused >>> .v-input__slot {
  border-color: #d7bd00;
  box-shadow: 0 0 0 2px rgba(253, 223, 0, 0.18) !important;
}

.schedule-picker-input.v-input--is-disabled >>> .v-input__slot {
  background: #f5f7f8 !important;
}

.schedule-error {
  margin: 6px 0 0 2px;
  color: #d32f2f;
  font-size: 12px;
}

.enabled-field {
  padding: 2px 12px 12px;
}

.enabled-field p {
  margin: 4px 0 0 34px;
  color: #90a4ae;
  font-size: 12px;
}

@media (max-width: 900px) {
  .summary-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .filter-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 600px) {
  .recommendation-header,
  .form-header,
  .filter-heading {
    align-items: flex-start;
    flex-direction: column;
  }

  .header-actions {
    justify-content: space-between;
    width: 100%;
  }

  .recommendation-content {
    padding: 16px;
  }

  .summary-grid,
  .filter-grid,
  .form-grid {
    grid-template-columns: 1fr;
  }

  .add-button {
    width: 100%;
  }
}
</style>
